import {
  type Observable,
  sampleTime,
  map,
  filter,
  connect,
  merge,
  debounceTime,
  distinctUntilChanged,
  share,
  BehaviorSubject,
} from 'rxjs';
import { type AudioLevelsMap } from './audioLevels';
import {
  detectLoudestSpeaker,
  someoneIsSpeaking,
} from './detectLoudestSpeaker';

const ACTIVE_SPEAKER_INTERVAL_TIME = 500;
/**
 * Vonage documentation recommends using 3 seconds
 * https://developer.vonage.com/en/video/guides/ui-customization/general-customization#adjusting-user-interface-based-on-audio-levels
 */
const ACTIVE_SPEAKER_IDLE_TIME = 3000;

/**
 * The active speaker's ID, or undefined if no one is speaking.
 */
export type ActiveSpeakerId = string | undefined;

/**
 * After a while, if no one is speaking, reset the active speaker value.
 */
const noSpeakerIfIdle = (
  loudestSpeaker: Observable<string>,
): Observable<undefined> =>
  loudestSpeaker.pipe(
    debounceTime(ACTIVE_SPEAKER_IDLE_TIME),
    map(() => undefined),
  );

export const observeActiveSpeaker = (
  audioLevels: Observable<AudioLevelsMap>,
): Observable<ActiveSpeakerId> => {
  return audioLevels.pipe(
    sampleTime(ACTIVE_SPEAKER_INTERVAL_TIME),
    /**
     * Detect the loudest speaker on the interval.
     */
    map(detectLoudestSpeaker),

    /**
     * Do not emit if no one is loud enough
     */
    filter(someoneIsSpeaking),

    /**
     * Emit 'undefined' if no one is speaking for a while.
     */
    connect((speaker) => merge(speaker, noSpeakerIfIdle(speaker))),

    /**
     * Only emit when the active speaker changes.
     */
    distinctUntilChanged(),

    /**
     * Support any number of observers.
     * If an observer subscribes later on, immediately emit the current active speaker value to them.
     */
    share({
      connector: () => new BehaviorSubject<ActiveSpeakerId>(undefined),
    }),
  );
};
